import { useEffect } from 'react';

/**
 * Upgrades any in-page anchor click with a smooth, cinematic scroll
 * animation. Works alongside the global `scroll-behavior: smooth` and
 * lengthens it for a more dramatic feel.
 */
export function useSmoothScroll(durationMs = 900) {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest('a[href^="#"]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const id = anchor.getAttribute('href')?.slice(1);
      if (!id) return;
      const el = document.getElementById(id);
      if (!el) return;
      e.preventDefault();

      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReduced) {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
        history.replaceState(null, '', `#${id}`);
        return;
      }

      // Offset for the sticky header; tighter on small screens.
      const headerOffset = window.innerWidth < 640 ? 72 : 88;
      const targetY = el.getBoundingClientRect().top + window.scrollY - headerOffset;
      const startY = window.scrollY;
      const distance = targetY - startY;
      const start = performance.now();

      function easeInOutCubic(t: number) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      }

      function step(now: number) {
        const t = Math.min(1, (now - start) / durationMs);
        const eased = easeInOutCubic(t);
        window.scrollTo(0, startY + distance * eased);
        if (t < 1) requestAnimationFrame(step);
        else history.replaceState(null, '', `#${id}`);
      }

      requestAnimationFrame(step);
    }

    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [durationMs]);
}
